import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { 
  Shield, Code, Zap, User, LogOut, Settings, FileText, Play, 
  Home, Moon, Sun, Copy, Check, Plus, Minus, RefreshCw, 
  BarChart3, Users, Key, Mail, Phone, Lock, Eye, EyeOff,
  ChevronLeft, ChevronRight, Search, Download, Calendar,
  CheckCircle, XCircle, Clock, Loader2, MessageCircle, X, Trash2, Menu, AlertCircle, Send
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Theme Context
const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");

  useEffect(() => {
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Auth Context
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const adminToken = localStorage.getItem("adminToken");
    
    if (adminToken) {
      setIsAdmin(true);
      setLoading(false);
    } else if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async (token) => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
    } catch (e) {
      localStorage.removeItem("token");
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    localStorage.setItem("token", res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const adminLogin = async (email, password) => {
    const res = await axios.post(`${API}/auth/admin/login`, { email, password });
    localStorage.setItem("adminToken", res.data.token);
    setIsAdmin(true);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("adminToken");
    setUser(null);
    setIsAdmin(false);
  };

  const getAuthHeader = () => {
    const adminToken = localStorage.getItem("adminToken");
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${adminToken || token}` };
  };

  return (
    <AuthContext.Provider value={{ user, isAdmin, loading, login, register, adminLogin, logout, getAuthHeader }}>
      {children}
    </AuthContext.Provider>
  );
};

// Product Categories - PUBG
const CATEGORIES = {
  1: { name: "10 UC", amount: 10 },
  2: { name: "60 UC", amount: 60 },
  3: { name: "325 UC", amount: 325 },
  4: { name: "660 UC", amount: 660 },
  5: { name: "1800 UC", amount: 1800 },
  6: { name: "3850 UC", amount: 3850 },
  7: { name: "8100 UC", amount: 8100 },
};

// Product Categories - Free Fire
const FF_CATEGORIES = {
  1: { name: "110 Diamonds", amount: 110 },
  2: { name: "230 Diamonds", amount: 230 },
  3: { name: "580 Diamonds", amount: 580 },
  4: { name: "1188 Diamonds", amount: 1188 },
  5: { name: "2040 Diamonds", amount: 2040 },
};

// Contact types with icons
const CONTACT_TYPES = {
  whatsapp: { name: "واتساب", icon: "https://cdn-icons-png.flaticon.com/512/124/124034.png", color: "#25D366" },
  telegram: { name: "تيليجرام", icon: "https://cdn-icons-png.flaticon.com/512/2111/2111646.png", color: "#0088cc" },
  instagram: { name: "انستجرام", icon: "https://cdn-icons-png.flaticon.com/512/174/174855.png", color: "#E4405F" },
  facebook: { name: "فيسبوك", icon: "https://cdn-icons-png.flaticon.com/512/124/124010.png", color: "#1877F2" },
  email: { name: "البريد", icon: "https://cdn-icons-png.flaticon.com/512/732/732200.png", color: "#EA4335" },
  x: { name: "X", icon: "https://cdn-icons-png.flaticon.com/512/5969/5969020.png", color: "#000000" },
};

// Floating Contact Button - Expanding circles
const FloatingContact = () => {
  const [contacts, setContacts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/contacts`).then(res => {
      setContacts(res.data.contacts || []);
    }).catch(() => {});
  }, []);

  if (contacts.length === 0) return null;

  return (
    <div className="fixed bottom-6 left-6 z-50">
      {/* Contact circles that appear when open */}
      {contacts.map((contact, i) => {
        const type = CONTACT_TYPES[contact.type];
        if (!type) return null;
        
        // Calculate position - vertical stack
        const offset = isOpen ? (i + 1) * 60 : 0;
        
        return (
          <a
            key={i}
            href={contact.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`absolute w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            style={{
              backgroundColor: type.color,
              bottom: offset,
              left: 0,
              transitionDelay: `${i * 50}ms`,
            }}
            data-testid={`contact-${contact.type}`}
          >
            <img src={type.icon} alt={type.name} className="w-6 h-6" />
          </a>
        );
      })}

      {/* Main green button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}`}
        data-testid="contact-float-btn"
      >
        {isOpen ? (
          <X className="h-7 w-7 text-white" />
        ) : (
          <MessageCircle className="h-7 w-7 text-white" />
        )}
      </button>
    </div>
  );
};

// Company Info Section
const CompanyInfo = () => {
  const [companyLogo, setCompanyLogo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/public`).then(res => {
      setCompanyLogo(res.data.company_logo);
    }).catch(() => {});
  }, []);

  return (
    <section className="py-8 border-t">
      <div className="container mx-auto px-4 text-center">
        <p className="text-sm text-muted-foreground mb-4">
          هذا الموقع تابع لأعمال شركة <span className="font-bold text-foreground">Technoperts</span>
        </p>
        {companyLogo && (
          <div className="flex justify-center">
            <img 
              src={companyLogo} 
              alt="Technoperts Logo" 
              className="h-12 md:h-16 object-contain opacity-80 hover:opacity-100 transition-opacity"
            />
          </div>
        )}
      </div>
    </section>
  );
};

// Announcements Banner (البنود)
const AnnouncementsBanner = () => {
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    axios.get(`${API}/announcements`).then(res => {
      setAnnouncements(res.data.announcements || []);
    }).catch(() => {});
  }, []);

  if (announcements.length === 0) return null;

  return (
    <section className="py-6 bg-primary/10 border-y border-primary/20">
      <div className="container mx-auto px-4">
        <div className="flex flex-col gap-3">
          {announcements.map((item, i) => (
            <div 
              key={i} 
              className="flex items-center gap-3 p-3 bg-background/80 rounded-lg shadow-sm"
            >
              <div className="w-2 h-2 bg-primary rounded-full shrink-0 animate-pulse" />
              <p className="text-sm font-medium">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Components
const Navbar = () => {
  const { theme, toggleTheme } = useTheme();
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const NavLink = ({ to, children, icon: Icon }) => (
    <button
      onClick={() => handleNavigate(to)}
      className={`flex items-center gap-3 w-full p-3 rounded-lg transition-colors ${
        isActive(to) ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
      }`}
    >
      {Icon && <Icon className="h-4 w-4" />}
      <span className="text-sm">{children}</span>
    </button>
  );

  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4">
        <div className="flex h-14 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <Shield className="h-6 w-6 md:h-8 md:w-8 text-primary" />
            <span className="text-lg md:text-xl font-bold">API-Redeem</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/" className={`text-xs transition-colors hover:text-primary ${isActive('/') ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
              الرئيسية
            </Link>
            <Link to="/docs" className={`text-xs transition-colors hover:text-primary ${isActive('/docs') ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
              التوثيق
            </Link>
            <Link to="/playground" className={`text-xs transition-colors hover:text-primary ${isActive('/playground') ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
              التجربة
            </Link>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle" className="h-8 w-8">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>

            {user ? (
              <>
                <Link to="/dashboard">
                  <Button variant="ghost" size="sm" className="text-xs h-8" data-testid="dashboard-btn">
                    <User className="h-3 w-3 ml-1" />
                    لوحة التحكم
                  </Button>
                </Link>
                <Button variant="ghost" size="sm" className="text-xs h-8" onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="h-3 w-3 ml-1" />
                  خروج
                </Button>
              </>
            ) : isAdmin ? (
              <>
                <Link to="/admin">
                  <Button variant="ghost" size="sm" className="text-xs h-8" data-testid="admin-panel-btn">
                    <Settings className="h-3 w-3 ml-1" />
                    لوحة الأدمن
                  </Button>
                </Link>
                <Button variant="ghost" size="sm" className="text-xs h-8" onClick={handleLogout} data-testid="admin-logout-btn">
                  <LogOut className="h-3 w-3 ml-1" />
                  خروج
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-xs h-8" data-testid="login-btn">دخول</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="text-xs h-8" data-testid="register-btn">إنشاء حساب</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="h-8 w-8">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2 justify-start">
                    <Shield className="h-6 w-6 text-primary" />
                    API-Redeem
                  </SheetTitle>
                </SheetHeader>
                
                <div className="mt-6 space-y-2">
                  <NavLink to="/" icon={Home}>الرئيسية</NavLink>
                  <NavLink to="/docs" icon={FileText}>التوثيق</NavLink>
                  <NavLink to="/playground" icon={Play}>التجربة</NavLink>
                  
                  <Separator className="my-4" />
                  
                  {user ? (
                    <>
                      <NavLink to="/dashboard" icon={User}>لوحة التحكم</NavLink>
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 w-full p-3 rounded-lg transition-colors hover:bg-muted text-red-500"
                      >
                        <LogOut className="h-4 w-4" />
                        <span className="text-sm">تسجيل الخروج</span>
                      </button>
                    </>
                  ) : isAdmin ? (
                    <>
                      <NavLink to="/admin" icon={Settings}>لوحة الأدمن</NavLink>
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 w-full p-3 rounded-lg transition-colors hover:bg-muted text-red-500"
                      >
                        <LogOut className="h-4 w-4" />
                        <span className="text-sm">تسجيل الخروج</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <NavLink to="/login" icon={User}>تسجيل الدخول</NavLink>
                      <button
                        onClick={() => handleNavigate('/register')}
                        className="flex items-center justify-center gap-2 w-full p-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                      >
                        <span className="text-sm font-medium">إنشاء حساب</span>
                      </button>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Landing Page
const LandingPage = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-12 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <Badge variant="secondary" className="mb-4 text-xs">
              <Zap className="h-3 w-3 ml-1" />
              بوابة آمنة ومشفرة
            </Badge>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 leading-tight">
              استبدال الأكواد بأمان وسرعة
            </h1>
            <p className="text-sm md:text-base text-muted-foreground mb-6 md:mb-8 px-4">
              منصة متكاملة لاستبدال أكواد UC مع API قوي وآمن
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {!user && !isAdmin ? (
                <>
                  <Button size="sm" className="text-xs" onClick={() => navigate('/register')} data-testid="hero-register-btn">
                    ابدأ الآن مجاناً
                  </Button>
                  <Button size="sm" variant="outline" className="text-xs" onClick={() => navigate('/docs')} data-testid="hero-docs-btn">
                    <FileText className="h-3 w-3 ml-1" />
                    اقرأ التوثيق
                  </Button>
                </>
              ) : (
                <Button size="sm" className="text-xs" onClick={() => navigate(isAdmin ? '/admin' : '/dashboard')} data-testid="hero-dashboard-btn">
                  الذهاب للوحة التحكم
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Announcements (البنود) */}
      <AnnouncementsBanner />

      {/* Features */}
      <section className="py-12 md:py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-center mb-8 md:mb-12">المميزات</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
            <Card className="border-0 shadow-lg">
              <CardHeader className="p-4">
                <Shield className="h-8 w-8 md:h-10 md:w-10 text-primary mb-2" />
                <CardTitle className="text-sm md:text-base">تشفير وحماية عالية</CardTitle>
                <CardDescription className="text-xs">
                  حماية متقدمة للمخزون
                </CardDescription>
              </CardHeader>
            </Card>
            <Card className="border-0 shadow-lg">
              <CardHeader className="p-4">
                <Zap className="h-8 w-8 md:h-10 md:w-10 text-primary mb-2" />
                <CardTitle className="text-sm md:text-base">استبدال فوري لحظي</CardTitle>
                <CardDescription className="text-xs">
                  نتائج لحظية مع تأكيد فوري
                </CardDescription>
              </CardHeader>
            </Card>
            <Card className="border-0 shadow-lg">
              <CardHeader className="p-4">
                <Code className="h-8 w-8 md:h-10 md:w-10 text-primary mb-2" />
                <CardTitle className="text-sm md:text-base">API سهل الاستخدام</CardTitle>
                <CardDescription className="text-xs">
                  ربط سهل مع البوتات
                </CardDescription>
              </CardHeader>
            </Card>
            <Card className="border-0 shadow-lg">
              <CardHeader className="p-4">
                <BarChart3 className="h-8 w-8 md:h-10 md:w-10 text-primary mb-2" />
                <CardTitle className="text-sm md:text-base">تقارير مفصلة</CardTitle>
                <CardDescription className="text-xs">
                  تتبع جميع العمليات
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Supported Games */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-center mb-4">الألعاب المدعومة</h2>
          <p className="text-center text-muted-foreground mb-8 text-xs md:text-sm">حالياً ندعم الألعاب التالية</p>
          <div className="flex justify-center gap-4 md:gap-8">
            <Card className="w-40 md:w-56 text-center hover:border-primary transition-colors" data-testid="game-pubg">
              <CardContent className="pt-6 p-4">
                <img 
                  src="https://play-lh.googleusercontent.com/JRd05pyBH41qjgsJuWduRJpDeZG0Hnb0yjf2nWqO7VaGKL10-G5UIygxED-WNOc3pg=w480-h960-rw" 
                  alt="PUBG Mobile" 
                  className="w-16 h-16 md:w-24 md:h-24 mx-auto mb-3 object-contain rounded-2xl"
                />
                <p className="text-sm md:text-xl font-bold">PUBG Mobile</p>
                <p className="text-xs text-muted-foreground">ببجي موبايل</p>
                <Badge className="mt-2 text-xs" variant="secondary">متاح</Badge>
              </CardContent>
            </Card>
            <Card className="w-40 md:w-56 text-center hover:border-orange-500 transition-colors" data-testid="game-freefire">
              <CardContent className="pt-6 p-4">
                <img 
                  src="https://play-lh.googleusercontent.com/pXooT00frDwY1CVPeN_Qiz1R4_UyHqhjdXQgP1-e8bf7VUGtYIly6K0R0p39jeoD80Yu3dPg7hqO4hi4oUVz=w240-h480-rw" 
                  alt="Free Fire" 
                  className="w-16 h-16 md:w-24 md:h-24 mx-auto mb-3 object-contain rounded-2xl"
                />
                <p className="text-sm md:text-xl font-bold">Free Fire</p>
                <p className="text-xs text-muted-foreground">فري فاير</p>
                <Badge className="mt-2 text-xs bg-orange-500">متاح</Badge>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Company Info */}
      <CompanyInfo />

      {/* Footer */}
      <footer className="py-6 border-t">
        <div className="container mx-auto px-4 text-center text-muted-foreground">
          <p className="text-xs">© 2024 API-Redeem. جميع الحقوق محفوظة.</p>
        </div>
      </footer>
    </div>
  );
};

// Auth Pages
const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("تم تسجيل الدخول بنجاح");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ في تسجيل الدخول");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center p-4">
          <Shield className="h-10 w-10 text-primary mx-auto mb-3" />
          <CardTitle className="text-lg">تسجيل الدخول</CardTitle>
          <CardDescription className="text-xs">أدخل بياناتك للوصول لحسابك</CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="email" className="text-xs">البريد الإلكتروني</Label>
              <div className="relative">
                <Mail className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="example@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pr-9 text-xs h-9"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="password" className="text-xs">كلمة المرور</Label>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-9 pl-9 text-xs h-9"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? <EyeOff className="h-3 w-3 text-muted-foreground" /> : <Eye className="h-3 w-3 text-muted-foreground" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full text-xs h-9" disabled={loading} data-testid="login-submit">
              {loading ? <Loader2 className="h-3 w-3 animate-spin ml-1" /> : null}
              تسجيل الدخول
            </Button>
          </form>
          <p className="text-center mt-3 text-xs text-muted-foreground">
            ليس لديك حساب؟{" "}
            <Link to="/register" className="text-primary hover:underline">إنشاء حساب</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    password: "",
    confirm_password: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirm_password) {
      toast.error("كلمتا المرور غير متطابقتان");
      return;
    }
    setLoading(true);
    try {
      await register(formData);
      toast.success("تم إنشاء الحساب بنجاح");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ في إنشاء الحساب");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center p-4">
          <Shield className="h-10 w-10 text-primary mx-auto mb-3" />
          <CardTitle className="text-lg">إنشاء حساب</CardTitle>
          <CardDescription className="text-xs">الحسابات مفعلة تلقائياً</CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="email" className="text-xs">البريد الإلكتروني</Label>
              <div className="relative">
                <Mail className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="example@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="pr-9 text-xs h-9"
                  required
                  data-testid="register-email"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="phone" className="text-xs">رقم الهاتف</Label>
              <div className="relative">
                <Phone className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+966xxxxxxxxx"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="pr-9 text-xs h-9"
                  required
                  data-testid="register-phone"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="password" className="text-xs">كلمة المرور</Label>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="pr-9 pl-9 text-xs h-9"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? <EyeOff className="h-3 w-3 text-muted-foreground" /> : <Eye className="h-3 w-3 text-muted-foreground" />}
                </button>
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="confirm_password" className="text-xs">تأكيد كلمة المرور</Label>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  id="confirm_password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                  className="pr-9 text-xs h-9"
                  required
                  data-testid="register-confirm-password"
                />
              </div>
            </div>
            <Button type="submit" className="w-full text-xs h-9" disabled={loading} data-testid="register-submit">
              {loading ? <Loader2 className="h-3 w-3 animate-spin ml-1" /> : null}
              إنشاء حساب
            </Button>
          </form>
          <p className="text-center mt-3 text-xs text-muted-foreground">
            لديك حساب؟{" "}
            <Link to="/login" className="text-primary hover:underline">تسجيل الدخول</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminLoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { adminLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(email, password);
      toast.success("تم تسجيل الدخول بنجاح");
      navigate("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ في تسجيل الدخول");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-background to-muted/50">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center p-4">
          <Settings className="h-10 w-10 text-primary mx-auto mb-3" />
          <CardTitle className="text-lg">لوحة الأدمن</CardTitle>
          <CardDescription className="text-xs">تسجيل دخول المشرف</CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="admin-email" className="text-xs">البريد الإلكتروني</Label>
              <div className="relative">
                <Mail className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  id="admin-email"
                  type="email"
                  placeholder="admin@api-redeem.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pr-9 text-xs h-9"
                  required
                  data-testid="admin-email"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="password" className="text-xs">كلمة المرور</Label>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-9 pl-9 text-xs h-9"
                  required
                  data-testid="admin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? <EyeOff className="h-3 w-3 text-muted-foreground" /> : <Eye className="h-3 w-3 text-muted-foreground" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full text-xs h-9" disabled={loading} data-testid="admin-login-submit">
              {loading ? <Loader2 className="h-3 w-3 animate-spin ml-1" /> : null}
              دخول
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// API Docs Page
const DocsPage = () => {
  const { theme } = useTheme();
  const [copied, setCopied] = useState(null);
  const [selectedLang, setSelectedLang] = useState("curl");

  // API URL for docs display
  const API_DOCS_URL = "https://api-redeem.tech";

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  // Code box style based on theme
  const codeBoxClass = theme === "dark" 
    ? "bg-white text-black" 
    : "bg-gray-900 text-white";

  // Code examples in multiple languages - with Query Params option
  const codeExamples = {
    checkPlayer: {
      curl: `# الطريقة 1: Query Parameters 
curl "${API_DOCS_URL}/api/v1/check-player?api_token=YOUR_TOKEN&player_id=51709255708"

# الطريقة 2: JSON Body
curl -X POST "${API_DOCS_URL}/api/v1/check-player?api_token=YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"player_id": "51709255708"}'`,
      python: `import requests

# الطريقة 1: Query Parameters 
url = "${API_DOCS_URL}/api/v1/check-player"
params = {
    "api_token": "YOUR_TOKEN",
    "player_id": "51709255708"
}
response = requests.get(url, params=params)
print(response.json())

# الطريقة 2: JSON Body
# response = requests.post(url, params={"api_token": "YOUR_TOKEN"}, json={"player_id": "51709255708"})`,
      javascript: `// الطريقة 1: Query Parameters 
const response = await fetch(
  "${API_DOCS_URL}/api/v1/check-player?api_token=YOUR_TOKEN&player_id=51709255708"
);
const data = await response.json();
console.log(data);

// الطريقة 2: JSON Body
// const response = await fetch(url, { method: "POST", headers: {...}, body: JSON.stringify({...}) });`
    },
    directRedeem: {
      curl: `# الطريقة 1: Query Parameters 
curl "${API_DOCS_URL}/api/v1/redeem/direct?api_token=YOUR_TOKEN&player_id=51709255708&code=XXXX-XXXX-XXXX"

# الطريقة 2: JSON Body
curl -X POST "${API_DOCS_URL}/api/v1/redeem/direct?api_token=YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"player_id": "51709255708", "code": "XXXX-XXXX-XXXX"}'`,
      python: `import requests

# الطريقة 1: Query Parameters 
url = "${API_DOCS_URL}/api/v1/redeem/direct"
params = {
    "api_token": "YOUR_TOKEN",
    "player_id": "51709255708",
    "code": "XXXX-XXXX-XXXX"
}
response = requests.get(url, params=params)
print(response.json())

# الطريقة 2: JSON Body
# response = requests.post(url, params={"api_token": "YOUR_TOKEN"}, json={...})`,
      javascript: `// الطريقة 1: Query Parameters 
const response = await fetch(
  "${API_DOCS_URL}/api/v1/redeem/direct?api_token=YOUR_TOKEN&player_id=51709255708&code=XXXX-XXXX-XXXX"
);
const data = await response.json();
console.log(data);

// الطريقة 2: JSON Body
// const response = await fetch(url, { method: "POST", ... });`
    },
    inventoryRedeem: {
      curl: `# الطريقة 1: Query Parameters 
curl "${API_DOCS_URL}/api/v1/redeem/inventory?api_token=YOUR_TOKEN&player_id=51709255708&product_id=2"

# الطريقة 2: JSON Body
curl -X POST "${API_DOCS_URL}/api/v1/redeem/inventory?api_token=YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"player_id": "51709255708", "product_id": 2}'`,
      python: `import requests

# الطريقة 1: Query Parameters 
url = "${API_DOCS_URL}/api/v1/redeem/inventory"
params = {
    "api_token": "YOUR_TOKEN",
    "player_id": "51709255708",
    "product_id": 2  # 60 UC
}
response = requests.get(url, params=params)
print(response.json())

# الطريقة 2: JSON Body
# response = requests.post(url, params={"api_token": "YOUR_TOKEN"}, json={...})`,
      javascript: `// الطريقة 1: Query Parameters 
const response = await fetch(
  "${API_DOCS_URL}/api/v1/redeem/inventory?api_token=YOUR_TOKEN&player_id=51709255708&product_id=2"
);
const data = await response.json();
console.log(data);

// الطريقة 2: JSON Body
// const response = await fetch(url, { method: "POST", ... });`
    },
    // Free Fire endpoints
    ffCheckPlayer: {
      curl: `# الطريقة 1: Query Parameters 
curl "${API_DOCS_URL}/api/v1/ff/check-player?api_token=YOUR_TOKEN&player_id=123456789"

# الطريقة 2: JSON Body
curl -X POST "${API_DOCS_URL}/api/v1/ff/check-player?api_token=YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"player_id": "123456789"}'`,
      python: `import requests

# الطريقة 1: Query Parameters 
url = "${API_DOCS_URL}/api/v1/ff/check-player"
params = {
    "api_token": "YOUR_TOKEN",
    "player_id": "123456789"
}
response = requests.get(url, params=params)
print(response.json())

# الطريقة 2: JSON Body
# response = requests.post(url, params={"api_token": "YOUR_TOKEN"}, json={"player_id": "123456789"})`,
      javascript: `// الطريقة 1: Query Parameters 
const response = await fetch(
  "${API_DOCS_URL}/api/v1/ff/check-player?api_token=YOUR_TOKEN&player_id=123456789"
);
const data = await response.json();
console.log(data);

// الطريقة 2: JSON Body
// const response = await fetch(url, { method: "POST", headers: {...}, body: JSON.stringify({...}) });`
    },
    ffDirectRedeem: {
      curl: `# الطريقة 1: Query Parameters 
curl "${API_DOCS_URL}/api/v1/ff/redeem/direct?api_token=YOUR_TOKEN&player_id=123456789&code=XXXX-XXXX-XXXX"

# الطريقة 2: JSON Body
curl -X POST "${API_DOCS_URL}/api/v1/ff/redeem/direct?api_token=YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"player_id": "123456789", "code": "XXXX-XXXX-XXXX"}'`,
      python: `import requests

# الطريقة 1: Query Parameters 
url = "${API_DOCS_URL}/api/v1/ff/redeem/direct"
params = {
    "api_token": "YOUR_TOKEN",
    "player_id": "123456789",
    "code": "XXXX-XXXX-XXXX"
}
response = requests.get(url, params=params)
print(response.json())

# الطريقة 2: JSON Body
# response = requests.post(url, params={"api_token": "YOUR_TOKEN"}, json={...})`,
      javascript: `// الطريقة 1: Query Parameters 
const response = await fetch(
  "${API_DOCS_URL}/api/v1/ff/redeem/direct?api_token=YOUR_TOKEN&player_id=123456789&code=XXXX-XXXX-XXXX"
);
const data = await response.json();
console.log(data);

// الطريقة 2: JSON Body
// const response = await fetch(url, { method: "POST", ... });`
    }
  };

  // Response examples
  const responseExamples = {
    checkPlayer: {
      success: `{
  "successful": true,
  "player_id": "51709255708",
  "name": "PlayerName"
}`,
      error: `{
  "successful": false,
  "player_id": "51709255708",
  "error": "اللاعب غير موجود"
}`
    },
    redeem: {
      success: `{
  "successful": true,
  "player_id": "51709255708",
  "code": "XXXX-****",
  "name": "PlayerName",
  "product_name": "60 UC"
}`,
      error: `{
  "successful": false,
  "player_id": "51709255708",
  "code": "XXXX-****",
  "error": "الكود غير صالح"
}`
    },
    ffCheckPlayer: {
      success: `{
  "successful": true,
  "player_id": "123456789",
  "name": "PlayerName"
}`,
      error: `{
  "successful": false,
  "player_id": "123456789",
  "error": "اللاعب غير موجود"
}`
    },
    ffRedeem: {
      success: `{
  "successful": true,
  "player_id": "123456789",
  "code": "XXXX-****",
  "name": "PlayerName",
  "product_name": "110 Diamonds"
}`,
      error: `{
  "successful": false,
  "player_id": "123456789",
  "code": "XXXX-****",
  "error": "الكود غير صالح"
}`
    }
  };

  const LanguageSelector = () => (
    <div className="flex gap-1 mb-3">
      {["curl", "python", "javascript"].map((lang) => (
        <Button
          key={lang}
          size="sm"
          variant={selectedLang === lang ? "default" : "outline"}
          onClick={() => setSelectedLang(lang)}
          className="text-xs h-7"
        >
          {lang === "curl" ? "cURL" : lang === "python" ? "Python" : "JavaScript"}
        </Button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen py-8 md:py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="mb-8 md:mb-12 text-center">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">توثيق API</h1>
          <p className="text-muted-foreground text-xs md:text-sm">دليل شامل لاستخدام واجهة البرمجة</p>
        </div>

        {/* Authentication */}
        <Card className="mb-6">
          <CardHeader className="p-4">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Key className="h-4 w-4 text-primary" />
              التوثيق
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <p className="text-xs">استخدم API Token الخاص بك مع كل طلب:</p>
            <div className={`rounded-lg p-3 font-mono text-xs ${codeBoxClass}`}>
              ?api_token=YOUR_API_TOKEN
            </div>
          </CardContent>
        </Card>

        {/* Request Methods Info */}
        <Card className="mb-6 border-primary/30 bg-primary/5">
          <CardHeader className="p-4">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Zap className="h-4 w-4 text-primary" />
              طريقتين لإرسال البيانات
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <Badge variant="default" className="text-xs shrink-0">الطريقة 1</Badge>
                <div>
                  <p className="text-xs font-medium">Query Parameters </p>
                  <code className="text-xs text-muted-foreground block mt-1" dir="ltr">
                    /check-player?api_token=XXX&player_id=123
                  </code>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Badge variant="outline" className="text-xs shrink-0">الطريقة 2</Badge>
                <div>
                  <p className="text-xs font-medium">JSON Body</p>
                  <code className="text-xs text-muted-foreground block mt-1" dir="ltr">
                    POST + {`{"player_id": "123"}`}
                  </code>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">💡 استخدم الطريقة الأنسب لك - كلاهما يعمل!</p>
          </CardContent>
        </Card>

        {/* Base URL */}
        <Card className="mb-6">
          <CardHeader className="p-4">
            <CardTitle className="text-sm">Base URL</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className={`flex items-center gap-2 rounded-lg p-3 ${codeBoxClass}`}>
              <code className="flex-1 font-mono text-xs">{API_DOCS_URL}/api/v1</code>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => copyToClipboard(`${API_DOCS_URL}/api/v1`, 'base')}
              >
                {copied === 'base' ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Endpoints */}
        <div className="space-y-6">
          {/* PUBG Section Header */}
          <Card className="border-blue-500/30 bg-blue-500/5">
            <CardHeader className="p-4">
              <CardTitle className="flex items-center gap-2 text-sm text-blue-500 font-bold">
                PUBG Mobile API
              </CardTitle>
              <CardDescription className="text-xs">نقاط النهاية الخاصة بببجي موبايل</CardDescription>
            </CardHeader>
          </Card>

          {/* Check Player */}
          <Card>
            <CardHeader className="p-4">
              <div className="flex items-center gap-2">
                <Badge className="text-xs">POST</Badge>
                <CardTitle className="text-sm">/check-player</CardTitle>
              </div>
              <CardDescription className="text-xs">التحقق من اسم اللاعب</CardDescription>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-4">
              <div>
                <h4 className="font-medium mb-2 text-xs">Body:</h4>
                <div className={`rounded-lg p-3 font-mono text-xs ${codeBoxClass}`} dir="ltr">
{`{"player_id": "51709255708"}`}
                </div>
              </div>
              
              <div>
                <h4 className="font-medium mb-2 text-xs">أمثلة الكود:</h4>
                <LanguageSelector />
                <div className={`relative rounded-lg p-3 ${codeBoxClass}`}>
                  <pre className="font-mono text-xs overflow-x-auto whitespace-pre-wrap" dir="ltr">{codeExamples.checkPlayer[selectedLang]}</pre>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 left-2 h-6 w-6"
                    onClick={() => copyToClipboard(codeExamples.checkPlayer[selectedLang], 'check')}
                  >
                    {copied === 'check' ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <h4 className="font-medium mb-2 text-xs text-green-500">✓ رد ناجح:</h4>
                  <div className="bg-green-500 text-white rounded-lg p-3">
                    <pre className="font-mono text-xs overflow-x-auto whitespace-pre-wrap" dir="ltr">{responseExamples.checkPlayer.success}</pre>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium mb-2 text-xs text-red-500">✗ رد خطأ:</h4>
                  <div className="bg-red-500 text-white rounded-lg p-3">
                    <pre className="font-mono text-xs overflow-x-auto whitespace-pre-wrap" dir="ltr">{responseExamples.checkPlayer.error}</pre>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Direct Redeem */}
          <Card>
            <CardHeader className="p-4">
              <div className="flex items-center gap-2">
                <Badge className="text-xs">POST</Badge>
                <CardTitle className="text-sm">/redeem/direct</CardTitle>
              </div>
              <CardDescription className="text-xs">استبدال مباشر بالكود</CardDescription>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-4">
              <div>
                <h4 className="font-medium mb-2 text-xs">Body:</h4>
                <div className={`rounded-lg p-3 font-mono text-xs ${codeBoxClass}`} dir="ltr">
{`{"player_id": "51709255708", "code": "XXXX-XXXX"}`}
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2 text-xs">أمثلة الكود:</h4>
                <LanguageSelector />
                <div className={`relative rounded-lg p-3 ${codeBoxClass}`}>
                  <pre className="font-mono text-xs overflow-x-auto whitespace-pre-wrap" dir="ltr">{codeExamples.directRedeem[selectedLang]}</pre>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 left-2 h-6 w-6"
                    onClick={() => copyToClipboard(codeExamples.directRedeem[selectedLang], 'direct')}
                  >
                    {copied === 'direct' ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <h4 className="font-medium mb-2 text-xs text-green-500">✓ رد ناجح:</h4>
                  <div className="bg-green-500 text-white rounded-lg p-3">
                    <pre className="font-mono text-xs overflow-x-auto whitespace-pre-wrap" dir="ltr">{responseExamples.redeem.success}</pre>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium mb-2 text-xs text-red-500">✗ رد خطأ:</h4>
                  <div className="bg-red-500 text-white rounded-lg p-3">
                    <pre className="font-mono text-xs overflow-x-auto whitespace-pre-wrap" dir="ltr">{responseExamples.redeem.error}</pre>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Error Codes */}
          <Card className="mt-8">
            <CardHeader className="p-4">
              <CardTitle className="flex items-center gap-2 text-sm text-orange-500 font-bold">
                Free Fire API
              </CardTitle>
              <CardDescription className="text-xs">نفس نظام ببجي - لكن لفري فاير</CardDescription>
            </CardHeader>
          </Card>

          {/* FF Check Player */}
          <Card>
            <CardHeader className="p-4">
              <div className="flex items-center gap-2">
                <Badge className="text-xs bg-orange-500">POST</Badge>
                <CardTitle className="text-sm">/ff/check-player</CardTitle>
              </div>
              <CardDescription className="text-xs">التحقق من اسم لاعب فري فاير</CardDescription>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-4">
              <div>
                <h4 className="font-medium mb-2 text-xs">Body:</h4>
                <div className={`rounded-lg p-3 font-mono text-xs ${codeBoxClass}`} dir="ltr">
{`{"player_id": "123456789"}`}
                </div>
              </div>
              
              <div>
                <h4 className="font-medium mb-2 text-xs">أمثلة الكود:</h4>
                <LanguageSelector />
                <div className={`relative rounded-lg p-3 ${codeBoxClass}`}>
                  <pre className="font-mono text-xs overflow-x-auto whitespace-pre-wrap" dir="ltr">{codeExamples.ffCheckPlayer[selectedLang]}</pre>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 left-2 h-6 w-6"
                    onClick={() => copyToClipboard(codeExamples.ffCheckPlayer[selectedLang], 'ffcheck')}
                  >
                    {copied === 'ffcheck' ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <h4 className="font-medium mb-2 text-xs text-green-500">رد ناجح:</h4>
                  <div className="bg-green-500 text-white rounded-lg p-3">
                    <pre className="font-mono text-xs overflow-x-auto whitespace-pre-wrap" dir="ltr">{responseExamples.ffCheckPlayer.success}</pre>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium mb-2 text-xs text-red-500">رد خطأ:</h4>
                  <div className="bg-red-500 text-white rounded-lg p-3">
                    <pre className="font-mono text-xs overflow-x-auto whitespace-pre-wrap" dir="ltr">{responseExamples.ffCheckPlayer.error}</pre>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* FF Direct Redeem */}
          <Card>
            <CardHeader className="p-4">
              <div className="flex items-center gap-2">
                <Badge className="text-xs bg-orange-500">POST</Badge>
                <CardTitle className="text-sm">/ff/redeem/direct</CardTitle>
              </div>
              <CardDescription className="text-xs">استبدال كود فري فاير مباشر</CardDescription>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-4">
              <div>
                <h4 className="font-medium mb-2 text-xs">Body:</h4>
                <div className={`rounded-lg p-3 font-mono text-xs ${codeBoxClass}`} dir="ltr">
{`{"player_id": "123456789", "code": "XXXX-XXXX"}`}
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2 text-xs">أمثلة الكود:</h4>
                <LanguageSelector />
                <div className={`relative rounded-lg p-3 ${codeBoxClass}`}>
                  <pre className="font-mono text-xs overflow-x-auto whitespace-pre-wrap" dir="ltr">{codeExamples.ffDirectRedeem[selectedLang]}</pre>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 left-2 h-6 w-6"
                    onClick={() => copyToClipboard(codeExamples.ffDirectRedeem[selectedLang], 'ffdirect')}
                  >
                    {copied === 'ffdirect' ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                  </Button>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <h4 className="font-medium mb-2 text-xs text-green-500">رد ناجح:</h4>
                  <div className="bg-green-500 text-white rounded-lg p-3">
                    <pre className="font-mono text-xs overflow-x-auto whitespace-pre-wrap" dir="ltr">{responseExamples.ffRedeem.success}</pre>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium mb-2 text-xs text-red-500">رد خطأ:</h4>
                  <div className="bg-red-500 text-white rounded-lg p-3">
                    <pre className="font-mono text-xs overflow-x-auto whitespace-pre-wrap" dir="ltr">{responseExamples.ffRedeem.error}</pre>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Error Codes */}
          <Card>
            <CardHeader className="p-4">
              <CardTitle className="flex items-center gap-2 text-sm">
                <XCircle className="h-4 w-4 text-red-500" />
                رموز الأخطاء (Error Codes)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="space-y-2">
                {[
                  { code: "ACCOUNT_DISABLED", msg: "الحساب غير مفعل - تواصل مع الأدمن", color: "red" },
                  { code: "ACCOUNT_EXPIRED", msg: "انتهت صلاحية الحساب", color: "red" },
                  { code: "REQUESTS_LIMIT", msg: "انتهى رصيد الطلبات", color: "red" },
                  { code: "INVALID_CODE", msg: "الكود غير صالح أو منتهي", color: "red" },
                  { code: "CHARACTER_NOT_FOUND", msg: "اللاعب غير موجود", color: "red" },
                  { code: "RISK_CONTROL", msg: "تم رفض العملية بسبب الحماية", color: "red" },
                  { code: "LOGIN_FAILED", msg: "فشل تسجيل الدخول", color: "red" },
                  { code: "NO_CODES_AVAILABLE", msg: "لا توجد أكواد في المخزون", color: "red" },
                  { code: "503", msg: "السيرفر مشغول", color: "yellow" },
                  { code: "SERVER_DOWN", msg: "السيرفر غير متاح", color: "yellow" },
                  { code: "TIMEOUT", msg: "انتهت مهلة الاتصال", color: "yellow" },
                ].map((err, i) => (
                  <div key={i} className={`flex justify-between items-center p-2 rounded-lg text-xs ${
                    err.color === "red" ? "bg-red-500/10 border border-red-500/20" : "bg-yellow-500/10 border border-yellow-500/20"
                  }`}>
                    <span className={`font-mono ${err.color === "red" ? "text-red-500" : "text-yellow-500"}`}>{err.code}</span>
                    <span className="text-muted-foreground">{err.msg}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Try Request Section */}
          <TryRequestSection codeBoxClass={codeBoxClass} />
        </div>
      </div>
    </div>
  );
};

// Try Request Component
const TryRequestSection = ({ codeBoxClass }) => {
  const [apiToken, setApiToken] = useState("");
  const [endpoint, setEndpoint] = useState("check-player");
  const [playerId, setPlayerId] = useState("");
  const [codeVal, setCodeVal] = useState("");
  const [productId, setProductId] = useState("1");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const endpoints = [
    { value: "check-player", label: "PUBG - تفقد لاعب", fields: ["player_id"] },
    { value: "redeem/direct", label: "PUBG - استبدال مباشر", fields: ["player_id", "code"] },
    { value: "ff/check-player", label: "Free Fire - تفقد لاعب", fields: ["player_id"] },
    { value: "ff/redeem/direct", label: "Free Fire - استبدال مباشر", fields: ["player_id", "code"] },
  ];

  const selectedEndpoint = endpoints.find(e => e.value === endpoint);

  const handleSend = async () => {
    if (!apiToken.trim()) {
      toast.error("أدخل التوكن");
      return;
    }
    if (!playerId.trim()) {
      toast.error("أدخل آيدي اللاعب");
      return;
    }
    setLoading(true);
    setResponse(null);
    try {
      const params = { api_token: apiToken, player_id: playerId };
      if (selectedEndpoint?.fields.includes("code")) params.code = codeVal;
      if (selectedEndpoint?.fields.includes("product_id")) params.product_id = parseInt(productId);

      const res = await axios.get(`${API}/v1/${endpoint}`, { params });
      setResponse({ status: res.status, data: res.data });
    } catch (err) {
      setResponse({
        status: err.response?.status || 500,
        data: err.response?.data || { error: "خطأ في الاتصال" }
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mt-4 border-primary/30">
      <CardHeader className="p-4">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Zap className="h-4 w-4 text-primary" />
          تجربة ريكويست
        </CardTitle>
        <CardDescription className="text-xs">جرب API مباشرة من هنا</CardDescription>
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-3">
        <div className="space-y-1">
          <Label className="text-xs">API Token</Label>
          <Input
            placeholder="أدخل التوكن"
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
            className="font-mono text-xs h-9"
            dir="ltr"
            data-testid="try-api-token"
          />
        </div>

        <div className="space-y-1">
          <Label className="text-xs">نوع الريكويست</Label>
          <select
            className="w-full h-9 text-xs border rounded px-2 bg-background"
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
            data-testid="try-endpoint"
          >
            {endpoints.map(ep => (
              <option key={ep.value} value={ep.value}>{ep.label}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs">آيدي اللاعب</Label>
          <Input
            placeholder="51709255708"
            value={playerId}
            onChange={(e) => setPlayerId(e.target.value)}
            className="font-mono text-xs h-9"
            dir="ltr"
            data-testid="try-player-id"
          />
        </div>

        {selectedEndpoint?.fields.includes("code") && (
          <div className="space-y-1">
            <Label className="text-xs">الكود</Label>
            <Input
              placeholder="XXXX-XXXX-XXXX"
              value={codeVal}
              onChange={(e) => setCodeVal(e.target.value)}
              className="font-mono text-xs h-9"
              dir="ltr"
              data-testid="try-code"
            />
          </div>
        )}

        {selectedEndpoint?.fields.includes("product_id") && (
          <div className="space-y-1">
            <Label className="text-xs">الفئة (product_id)</Label>
            <select
              className="w-full h-9 text-xs border rounded px-2 bg-background"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              data-testid="try-product-id"
            >
              {Object.entries(CATEGORIES).map(([id, cat]) => (
                <option key={id} value={id}>{id} - {cat.name}</option>
              ))}
            </select>
          </div>
        )}

        <Button
          onClick={handleSend}
          className="w-full text-xs h-9"
          disabled={loading}
          data-testid="try-send-btn"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin ml-2" /> : <Send className="h-3 w-3 ml-2" />}
          إرسال
        </Button>

        {response && (
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-2">
              <Label className="text-xs">الرد</Label>
              <Badge variant={response.status < 400 ? "default" : "destructive"} className="text-xs">
                {response.status}
              </Badge>
            </div>
            <div className={`rounded-lg p-3 ${codeBoxClass}`}>
              <pre className="font-mono text-xs overflow-x-auto whitespace-pre-wrap" dir="ltr">
                {JSON.stringify(response.data, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
const PlaygroundPage = () => {
  const { getAuthHeader, user } = useAuth();
  const [tab, setTab] = useState("check");
  const [playerId, setPlayerId] = useState("");
  const [code, setCode] = useState("");
  const [productId, setProductId] = useState("2");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleCheckPlayer = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/redeem/check-player`, 
        { player_id: playerId },
        { headers: getAuthHeader() }
      );
      setResult({ success: true, data: res.data });
      toast.success("تم العثور على اللاعب");
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.detail || "خطأ" });
      toast.error(err.response?.data?.detail || "خطأ");
    } finally {
      setLoading(false);
    }
  };

  const handleDirectRedeem = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/redeem/direct`, 
        { player_id: playerId, code },
        { headers: getAuthHeader() }
      );
      setResult({ success: true, data: res.data });
      toast.success("تم الاستبدال بنجاح");
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.detail || "خطأ" });
      toast.error(err.response?.data?.detail || "خطأ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-8 md:py-12">
      <div className="container mx-auto px-4 max-w-lg">
        <div className="mb-6 text-center">
          <h1 className="text-xl md:text-2xl font-bold mb-2">صفحة التجربة</h1>
          <p className="text-muted-foreground text-xs">جرب الـ API مباشرة</p>
        </div>

        <Card>
          <CardContent className="p-4">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid grid-cols-2 mb-4 h-8">
                <TabsTrigger value="check" className="text-xs" data-testid="tab-check">تفقد لاعب</TabsTrigger>
                <TabsTrigger value="direct" className="text-xs" data-testid="tab-direct">استبدال مباشر</TabsTrigger>
              </TabsList>

              <TabsContent value="check" className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">آيدي اللاعب</Label>
                  <Input
                    placeholder="51709255708"
                    value={playerId}
                    onChange={(e) => setPlayerId(e.target.value)}
                    className="text-xs h-9"
                    data-testid="check-player-id"
                  />
                </div>
                <Button 
                  className="w-full text-xs h-9" 
                  onClick={handleCheckPlayer} 
                  disabled={loading || !playerId}
                  data-testid="check-submit"
                >
                  {loading ? <Loader2 className="h-3 w-3 animate-spin ml-1" /> : <Search className="h-3 w-3 ml-1" />}
                  تفقد
                </Button>
              </TabsContent>

              <TabsContent value="direct" className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">آيدي اللاعب</Label>
                  <Input
                    placeholder="51709255708"
                    value={playerId}
                    onChange={(e) => setPlayerId(e.target.value)}
                    className="text-xs h-9"
                    data-testid="direct-player-id"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">الكود</Label>
                  <Input
                    placeholder="XXXX-XXXX-XXXX-XXXX"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="text-xs h-9"
                    data-testid="direct-code"
                  />
                </div>
                <Button 
                  className="w-full text-xs h-9" 
                  onClick={handleDirectRedeem} 
                  disabled={loading || !playerId || !code}
                  data-testid="direct-submit"
                >
                  {loading ? <Loader2 className="h-3 w-3 animate-spin ml-1" /> : <Zap className="h-3 w-3 ml-1" />}
                  استبدال
                </Button>
              </TabsContent>

            </Tabs>

            {/* Result */}
            {result && (
              <div className={`mt-4 p-3 rounded-lg text-white ${result.success ? 'bg-green-500' : 'bg-red-500'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {result.success ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <XCircle className="h-4 w-4" />
                  )}
                  <span className="font-medium text-xs">{result.success ? 'نجاح' : 'خطأ'}</span>
                </div>
                <pre className="font-mono text-xs overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(result.success ? result.data : { error: result.error }, null, 2)}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// User Dashboard
const DashboardPage = () => {
  const { user, getAuthHeader } = useAuth();
  const [reports, setReports] = useState(null);
  const [reportPeriod, setReportPeriod] = useState("day");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const { theme } = useTheme();

  const codeBoxClass = theme === "dark" ? "bg-white text-black" : "bg-gray-900 text-white";

  useEffect(() => {
    fetchData();
  }, [reportPeriod]);

  const fetchData = async () => {
    try {
      const repRes = await axios.get(`${API}/reports?period=${reportPeriod}`, { headers: getAuthHeader() });
      setReports(repRes.data);
    } catch (err) {
      toast.error("خطأ في تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  const copyToken = () => {
    navigator.clipboard.writeText(user?.api_token || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-6 md:py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold mb-1">لوحة التحكم</h1>
          <p className="text-muted-foreground text-xs">{user?.email}</p>
        </div>

        {/* Account Status Banner */}
        <Card className={`mb-6 ${user?.is_trial ? 'border-yellow-500/50 bg-yellow-500/5' : user?.is_active ? 'border-green-500/50 bg-green-500/5' : 'border-red-500/50 bg-red-500/5'}`}>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user?.is_trial ? 'bg-yellow-500/20' : user?.is_active ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                  {user?.is_trial ? (
                    <Clock className="h-5 w-5 text-yellow-500" />
                  ) : user?.is_active ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                </div>
                <div>
                  <p className="font-bold text-sm">
                    {user?.is_trial ? 'فترة تجريبية' : user?.is_active ? 'حساب مفعل' : 'حساب غير مفعل'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {user?.is_trial ? (
                      <>
                        متبقي {user?.trial_count || 0} طلب تجريبي
                        {user?.trial_expires_at && (
                          <> • تنتهي {new Date(user.trial_expires_at).toLocaleDateString('ar')}</>
                        )}
                      </>
                    ) : user?.is_active ? (
                      user?.subscription_type === "daily" ? (
                        <>
                          اشتراك يومي • متبقي {Math.max(0, (user?.daily_requests_limit || 0) - (user?.daily_requests_used || 0))} من {user?.daily_requests_limit || 0} طلب اليوم
                        </>
                      ) : (
                        user?.max_requests ? `متبقي ${user.max_requests} طلب` : 'غير محدود'
                      )
                    ) : (
                      'تواصل مع الأدمن للتفعيل'
                    )}
                  </p>
                </div>
              </div>
              <Badge variant={user?.is_trial ? "outline" : user?.is_active ? "default" : "destructive"} className="text-xs w-fit">
                {user?.is_trial ? '🕐 تجريبي' : user?.is_active ? '✓ مفعل' : '✗ غير مفعل'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card>
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground">الحالة</p>
              <Badge variant={user?.is_active || user?.is_trial ? "default" : "destructive"} className="mt-1 text-xs">
                {user?.is_trial ? "تجريبي" : user?.is_active ? "مفعل" : "غير مفعل"}
              </Badge>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground">إجمالي الطلبات</p>
              <p className="text-lg font-bold">{reports?.total_requests || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground">ناجحة</p>
              <p className="text-lg font-bold text-green-500">{reports?.successful || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground">فاشلة</p>
              <p className="text-lg font-bold text-red-500">{reports?.failed || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* API Token */}
        <Card className="mb-6">
          <CardHeader className="p-4">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Key className="h-4 w-4" />
              API Token
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className={`flex items-center gap-2 rounded-lg p-2 ${codeBoxClass}`}>
              <Input 
                value={user?.api_token || ""} 
                readOnly 
                className="font-mono text-xs border-0 bg-transparent h-7"
                data-testid="api-token-input"
              />
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={copyToken} data-testid="copy-token-btn">
                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Reports */}
        <Card>
          <CardHeader className="p-4 flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-sm">
              <BarChart3 className="h-4 w-4" />
              التقارير
            </CardTitle>
            <Select value={reportPeriod} onValueChange={setReportPeriod}>
              <SelectTrigger className="w-24 text-xs h-7" data-testid="report-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day" className="text-xs">يومي</SelectItem>
                <SelectItem value="week" className="text-xs">أسبوعي</SelectItem>
                <SelectItem value="month" className="text-xs">شهري</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <ScrollArea className="h-64">
              <div className="space-y-2">
                {reports?.logs?.map((log, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      {log.success !== false ? (
                        <CheckCircle className="h-3 w-3 text-green-500" />
                      ) : (
                        <XCircle className="h-3 w-3 text-red-500" />
                      )}
                      <div>
                        <p className="font-medium text-xs">{log.type}</p>
                        <p className="text-xs text-muted-foreground">{log.player_id}</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {new Date(log.created_at).toLocaleDateString('ar')}
                    </p>
                  </div>
                ))}
                {(!reports?.logs || reports.logs.length === 0) && (
                  <p className="text-center text-muted-foreground py-6 text-xs">لا توجد سجلات</p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const { getAuthHeader } = useAuth();
  const { theme } = useTheme();
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userReports, setUserReports] = useState([]);
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [editData, setEditData] = useState({});
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [logSearchQuery, setLogSearchQuery] = useState("");
  
  // API Tokens state
  const [apiTokens, setApiTokens] = useState([]);
  const [newToken, setNewToken] = useState("");
  
  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [newContact, setNewContact] = useState({ type: "whatsapp", url: "" });
  
  // Company logo state
  const [companyLogo, setCompanyLogo] = useState("");
  
  // Announcements state (البنود)
  const [announcements, setAnnouncements] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState("");
  
  // Trial settings
  const [defaultTrialCount, setDefaultTrialCount] = useState(5);
  const [trialDurationHours, setTrialDurationHours] = useState(24);
  
  // Telegram bot settings
  const [telegramBotToken, setTelegramBotToken] = useState("");
  const [telegramChatId, setTelegramChatId] = useState("");
  const [telegramAdminIds, setTelegramAdminIds] = useState("");

  const codeBoxClass = theme === "dark" ? "bg-white text-black" : "bg-gray-900 text-white";
  
  // Filter users based on search
  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.id?.toString().includes(searchQuery) ||
    user.phone?.includes(searchQuery)
  );
  
  // Filter logs based on search
  const filteredLogs = userReports.filter(log =>
    log.player_id?.includes(logSearchQuery) ||
    log.type?.toLowerCase().includes(logSearchQuery.toLowerCase()) ||
    log.error_message?.includes(logSearchQuery)
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/settings`, { headers: getAuthHeader() })
      ]);
      setUsers(usersRes.data.users);
      setSettings(settingsRes.data);
      setApiTokens(settingsRes.data.kokos_api_tokens || []);
      setContacts(settingsRes.data.contacts || []);
      setCompanyLogo(settingsRes.data.company_logo || "");
      setAnnouncements(settingsRes.data.announcements || []);
      setDefaultTrialCount(settingsRes.data.default_trial_count || 5);
      setTrialDurationHours(settingsRes.data.trial_duration_hours || 24);
      setTelegramBotToken(settingsRes.data.telegram_bot_token || "");
      setTelegramChatId(settingsRes.data.telegram_chat_id || "");
      setTelegramAdminIds(settingsRes.data.telegram_admin_ids || "");
    } catch (err) {
      toast.error("خطأ في تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserReports = async (userId, search = "") => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/reports?search=${encodeURIComponent(search)}`, { headers: getAuthHeader() });
      setUserReports(res.data.logs);
    } catch (err) {
      toast.error("خطأ");
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setLogSearchQuery("");
    fetchUserReports(user.id);
  };
  
  // Handle log search
  const handleLogSearch = (query) => {
    setLogSearchQuery(query);
    if (selectedUser) {
      fetchUserReports(selectedUser.id, query);
    }
  };

  // Adjust requests (add/subtract)
  const handleAdjustRequests = async (amount, field = "max_requests") => {
    if (!selectedUser) return;
    try {
      const res = await axios.post(`${API}/admin/users/${selectedUser.id}/adjust-requests`, {
        amount,
        field
      }, { headers: getAuthHeader() });
      toast.success(res.data.message);
      fetchData();
      // Update selected user locally
      setSelectedUser(prev => ({
        ...prev,
        [field]: res.data.new_value
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ");
    }
  };

  const handleUpdateUser = async () => {
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}`, editData, { headers: getAuthHeader() });
      toast.success("تم التحديث");
      setEditUserDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ");
    }
  };

  const handleChangePassword = async (random = false) => {
    try {
      const res = await axios.post(`${API}/admin/users/${selectedUser.id}/password`, {
        random,
        new_password: random ? null : newPassword
      }, { headers: getAuthHeader() });
      toast.success(`كلمة المرور الجديدة: ${res.data.new_password}`);
      setPasswordDialog(false);
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ");
    }
  };

  const handleAddToken = () => {
    if (newToken.trim()) {
      setApiTokens([...apiTokens, newToken.trim()]);
      setNewToken("");
    }
  };

  const handleRemoveToken = (index) => {
    setApiTokens(apiTokens.filter((_, i) => i !== index));
  };

  const handleAddContact = () => {
    if (newContact.url.trim()) {
      setContacts([...contacts, { ...newContact }]);
      setNewContact({ type: "whatsapp", url: "" });
    }
  };

  const handleRemoveContact = (index) => {
    setContacts(contacts.filter((_, i) => i !== index));
  };

  // Announcements handlers (البنود)
  const handleAddAnnouncement = () => {
    if (newAnnouncement.trim()) {
      setAnnouncements([...announcements, { text: newAnnouncement.trim(), active: true }]);
      setNewAnnouncement("");
    }
  };

  const handleRemoveAnnouncement = (index) => {
    setAnnouncements(announcements.filter((_, i) => i !== index));
  };

  const handleToggleAnnouncement = (index) => {
    setAnnouncements(announcements.map((a, i) => 
      i === index ? { ...a, active: !a.active } : a
    ));
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/admin/settings`, {
        kokos_api_tokens: apiTokens,
        contacts,
        company_logo: companyLogo || null,
        announcements,
        default_trial_count: parseInt(defaultTrialCount) || 5,
        trial_duration_hours: parseInt(trialDurationHours) || 24,
        telegram_bot_token: telegramBotToken || null,
        telegram_chat_id: telegramChatId || null,
        telegram_admin_ids: telegramAdminIds || null
      }, { headers: getAuthHeader() });
      toast.success("تم حفظ الإعدادات");
      setSettingsDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "خطأ");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-6 md:py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold mb-1">لوحة الأدمن</h1>
            <p className="text-muted-foreground text-xs">إدارة المستخدمين والإعدادات</p>
          </div>
          <Dialog open={settingsDialog} onOpenChange={setSettingsDialog}>
            <DialogTrigger asChild>
              <Button size="sm" className="text-xs h-8" data-testid="admin-settings-btn">
                <Settings className="h-3 w-3 ml-1" />
                الإعدادات
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-sm">إعدادات النظام</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* API Tokens */}
                <div className="space-y-2">
                  <Label className="text-xs font-bold">API Tokens</Label>
                  <p className="text-xs text-muted-foreground">يمكن إضافة أكثر من token للتوزيع التلقائي</p>
                  
                  {apiTokens.map((token, i) => (
                    <div key={i} className={`flex items-center gap-2 p-2 rounded ${codeBoxClass}`}>
                      <code className="flex-1 text-xs truncate">{token.slice(0, 15)}...{token.slice(-5)}</code>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleRemoveToken(i)}>
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    </div>
                  ))}
                  
                  <div className="flex gap-2">
                    <Input
                      placeholder="أدخل Token جديد"
                      value={newToken}
                      onChange={(e) => setNewToken(e.target.value)}
                      className="text-xs h-8"
                      type="text"
                    />
                    <Button size="sm" className="h-8 text-xs" onClick={handleAddToken}>
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                <Separator />
                
                {/* Trial Settings */}
                <div className="space-y-2">
                  <Label className="text-xs font-bold">إعدادات الفترة التجريبية</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">عدد الطلبات التجريبية</Label>
                      <Input
                        type="number"
                        value={defaultTrialCount}
                        onChange={(e) => setDefaultTrialCount(e.target.value)}
                        className="text-xs h-8 mt-1"
                        min="0"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">مدة التجربة (ساعة)</Label>
                      <Input
                        type="number"
                        value={trialDurationHours}
                        onChange={(e) => setTrialDurationHours(e.target.value)}
                        className="text-xs h-8 mt-1"
                        min="1"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Contact Methods */}
                <div className="space-y-2">
                  <Label className="text-xs font-bold">طرق التواصل</Label>
                  
                  {contacts.map((contact, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-muted rounded">
                      <img src={CONTACT_TYPES[contact.type]?.icon} alt="" className="w-5 h-5" />
                      <span className="flex-1 text-xs truncate">{contact.url}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleRemoveContact(i)}>
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    </div>
                  ))}
                  
                  <div className="flex gap-2">
                    <Select value={newContact.type} onValueChange={(v) => setNewContact({...newContact, type: v})}>
                      <SelectTrigger className="w-28 text-xs h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(CONTACT_TYPES).map(([key, val]) => (
                          <SelectItem key={key} value={key} className="text-xs">{val.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="الرابط"
                      value={newContact.url}
                      onChange={(e) => setNewContact({...newContact, url: e.target.value})}
                      className="text-xs h-8"
                    />
                    <Button size="sm" className="h-8 text-xs" onClick={handleAddContact}>
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                
                {/* Announcements (البنود) */}
                <div className="space-y-2 pt-4 border-t">
                  <Label className="text-xs font-bold">البنود (تظهر قبل الميزات)</Label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {announcements.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 bg-muted rounded text-xs">
                        <button
                          onClick={() => handleToggleAnnouncement(i)}
                          className={`w-3 h-3 rounded-full shrink-0 ${item.active ? 'bg-green-500' : 'bg-gray-400'}`}
                        />
                        <span className={`flex-1 ${!item.active ? 'line-through text-muted-foreground' : ''}`}>
                          {item.text}
                        </span>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-6 w-6 p-0 text-red-500"
                          onClick={() => handleRemoveAnnouncement(i)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="أضف بند جديد..."
                      value={newAnnouncement}
                      onChange={(e) => setNewAnnouncement(e.target.value)}
                      className="text-xs h-8"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddAnnouncement()}
                    />
                    <Button size="sm" className="h-8 text-xs" onClick={handleAddAnnouncement}>
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                
                {/* Company Logo */}
                <div className="space-y-2 pt-4 border-t">
                  <Label className="text-xs font-bold">لوغو الشركة (Technoperts)</Label>
                  <Input
                    placeholder="رابط صورة اللوغو"
                    value={companyLogo}
                    onChange={(e) => setCompanyLogo(e.target.value)}
                    className="text-xs h-8"
                    dir="ltr"
                    data-testid="company-logo-input"
                  />
                  {companyLogo && (
                    <div className="p-2 bg-muted rounded flex justify-center">
                      <img src={companyLogo} alt="Logo Preview" className="h-10 object-contain" />
                    </div>
                  )}
                </div>
                
                {/* Telegram Bot Settings */}
                <div className="space-y-2 pt-4 border-t">
                  <Label className="text-xs font-bold">🤖 إشعارات تيليجرام</Label>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Bot Token</Label>
                      <Input
                        placeholder="توكن البوت من @BotFather"
                        value={telegramBotToken}
                        onChange={(e) => setTelegramBotToken(e.target.value)}
                        className="text-xs h-8 mt-1"
                        dir="ltr"
                        type="password"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Chat ID (المجموعة)</Label>
                      <Input
                        placeholder="آيدي المجموعة (مثال: -1001234567890)"
                        value={telegramChatId}
                        onChange={(e) => setTelegramChatId(e.target.value)}
                        className="text-xs h-8 mt-1"
                        dir="ltr"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">آيدي أدمن البوت (مفصولة بفاصلة)</Label>
                      <Input
                        placeholder="7007954041,123456789"
                        value={telegramAdminIds}
                        onChange={(e) => setTelegramAdminIds(e.target.value)}
                        className="text-xs h-8 mt-1"
                        dir="ltr"
                        data-testid="telegram-admin-ids"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      سيتم إرسال إشعارات عند: تسجيل مستخدم جديد، كل طلب API. بوت الإدارة يعمل بنفس التوكن ويستجيب فقط لآيديات الأدمن
                    </p>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleSaveSettings} className="text-xs h-8" data-testid="settings-save-btn">حفظ</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card>
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground">المستخدمين</p>
              <p className="text-lg font-bold">{users.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground">مفعلين</p>
              <p className="text-lg font-bold text-green-500">{users.filter(u => u.is_active).length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground">معطلين</p>
              <p className="text-lg font-bold text-red-500">{users.filter(u => !u.is_active).length}</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
          {/* Users List */}
          <Card className="lg:col-span-1">
            <CardHeader className="p-4">
              <CardTitle className="text-sm">المستخدمين</CardTitle>
              {/* Search Input */}
              <Input
                placeholder="بحث بالإيميل أو ID أو الهاتف..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="text-xs h-8 mt-2"
              />
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {filteredUsers.map((user) => (
                    <div
                      key={user.id}
                      className={`p-2 rounded-lg cursor-pointer transition-colors ${
                        selectedUser?.id === user.id ? 'bg-primary/10 border border-primary/20' : 'bg-muted/50 hover:bg-muted'
                      }`}
                      onClick={() => handleSelectUser(user)}
                      data-testid={`user-item-${user.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-xs">{user.email}</p>
                          <p className="text-xs text-muted-foreground">ID: {user.id}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge variant={user.is_trial ? "outline" : user.is_active ? "default" : "destructive"} className="text-xs">
                            {user.is_trial ? "تجريبي" : user.is_active ? "مفعل" : "معطل"}
                          </Badge>
                          {user.is_trial && (
                            <span className="text-xs text-muted-foreground">{user.trial_count} طلب</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {filteredUsers.length === 0 && (
                    <p className="text-center text-muted-foreground text-xs py-4">لا توجد نتائج</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* User Details */}
          <Card className="lg:col-span-2">
            <CardHeader className="p-4 flex flex-row items-center justify-between">
              <CardTitle className="text-sm">تفاصيل المستخدم</CardTitle>
              {selectedUser && (
                <div className="flex gap-2">
                  <Dialog open={editUserDialog} onOpenChange={setEditUserDialog}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" className="text-xs h-7" data-testid="edit-user-btn">
                        <Settings className="h-3 w-3 ml-1" />
                        تعديل
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-sm max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-sm">تعديل المستخدم</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        {/* Quick Actions - Add/Subtract Requests */}
                        <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
                          <Label className="text-xs font-bold">التحكم السريع بالطلبات</Label>
                          
                          {/* For trial users */}
                          {selectedUser.is_trial && (
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">الطلبات التجريبية ({selectedUser.trial_count || 0})</Label>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" className="h-8 text-xs flex-1" onClick={() => handleAdjustRequests(-1, "trial_count")} data-testid="sub-trial-btn">
                                  <Minus className="h-3 w-3 ml-1" /> -1
                                </Button>
                                <Button size="sm" variant="outline" className="h-8 text-xs flex-1" onClick={() => handleAdjustRequests(-5, "trial_count")}>
                                  -5
                                </Button>
                                <Button size="sm" variant="outline" className="h-8 text-xs flex-1" onClick={() => handleAdjustRequests(5, "trial_count")}>
                                  +5
                                </Button>
                                <Button size="sm" variant="outline" className="h-8 text-xs flex-1" onClick={() => handleAdjustRequests(1, "trial_count")} data-testid="add-trial-btn">
                                  +1 <Plus className="h-3 w-3 mr-1" />
                                </Button>
                              </div>
                            </div>
                          )}
                          
                          {/* For active users */}
                          {!selectedUser.is_trial && (
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">عدد الطلبات ({selectedUser.max_requests ?? 'غير محدود'})</Label>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" className="h-8 text-xs flex-1" onClick={() => handleAdjustRequests(-10, "max_requests")} data-testid="sub-requests-btn">
                                  <Minus className="h-3 w-3 ml-1" /> -10
                                </Button>
                                <Button size="sm" variant="outline" className="h-8 text-xs flex-1" onClick={() => handleAdjustRequests(-50, "max_requests")}>
                                  -50
                                </Button>
                                <Button size="sm" variant="outline" className="h-8 text-xs flex-1" onClick={() => handleAdjustRequests(50, "max_requests")}>
                                  +50
                                </Button>
                                <Button size="sm" variant="outline" className="h-8 text-xs flex-1" onClick={() => handleAdjustRequests(10, "max_requests")} data-testid="add-requests-btn">
                                  +10 <Plus className="h-3 w-3 mr-1" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <Separator />
                        
                        {/* Status Toggle */}
                        <div className="flex items-center justify-between">
                          <Label className="text-xs">الحالة (مفعل)</Label>
                          <Switch
                            checked={editData.is_active ?? selectedUser.is_active}
                            onCheckedChange={(v) => setEditData({...editData, is_active: v})}
                            data-testid="edit-user-active"
                          />
                        </div>
                        
                        {/* Trial Toggle */}
                        <div className="flex items-center justify-between">
                          <Label className="text-xs">وضع التجربة</Label>
                          <Switch
                            checked={editData.is_trial ?? selectedUser.is_trial}
                            onCheckedChange={(v) => setEditData({...editData, is_trial: v})}
                            data-testid="edit-user-trial"
                          />
                        </div>
                        
                        {/* Trial Count (if trial mode) */}
                        {(editData.is_trial ?? selectedUser.is_trial) && (
                          <div className="space-y-1">
                            <Label className="text-xs">عدد الطلبات التجريبية</Label>
                            <Input
                              type="number"
                              placeholder="5"
                              defaultValue={selectedUser.trial_count}
                              onChange={(e) => setEditData({...editData, trial_count: parseInt(e.target.value) || 0})}
                              className="text-xs h-8"
                              data-testid="edit-user-trial-count"
                            />
                          </div>
                        )}
                        
                        {/* Max Requests (if not trial) */}
                        {!(editData.is_trial ?? selectedUser.is_trial) && (
                          <>
                            {/* Subscription Type */}
                            <div className="space-y-1">
                              <Label className="text-xs">نوع الاشتراك</Label>
                              <select
                                className="w-full h-8 text-xs border rounded px-2"
                                value={editData.subscription_type ?? selectedUser.subscription_type ?? "requests"}
                                onChange={(e) => setEditData({...editData, subscription_type: e.target.value})}
                                data-testid="edit-user-subscription-type"
                              >
                                <option value="requests">عدد طلبات ثابت</option>
                                <option value="daily">اشتراك يومي (شهري)</option>
                              </select>
                            </div>
                            
                            {(editData.subscription_type ?? selectedUser.subscription_type) === "daily" ? (
                              <div className="space-y-1">
                                <Label className="text-xs">عدد الطلبات اليومية</Label>
                                <Input
                                  type="number"
                                  placeholder="50"
                                  defaultValue={selectedUser.daily_requests_limit}
                                  onChange={(e) => setEditData({...editData, daily_requests_limit: parseInt(e.target.value) || 0})}
                                  className="text-xs h-8"
                                  data-testid="edit-user-daily-limit"
                                />
                                <p className="text-xs text-muted-foreground">يتجدد كل 24 ساعة</p>
                              </div>
                            ) : (
                              <div className="space-y-1">
                                <Label className="text-xs">عدد الطلبات (فارغ = غير محدود)</Label>
                                <Input
                                  type="number"
                                  placeholder="غير محدود"
                                  defaultValue={selectedUser.max_requests}
                                  onChange={(e) => setEditData({...editData, max_requests: e.target.value ? parseInt(e.target.value) : null})}
                                  className="text-xs h-8"
                                  data-testid="edit-user-requests"
                                />
                              </div>
                            )}
                          </>
                        )}
                        
                        <div className="space-y-1">
                          <Label className="text-xs">تاريخ الانتهاء</Label>
                          <Input
                            type="datetime-local"
                            defaultValue={selectedUser.expires_at?.slice(0, 16)}
                            onChange={(e) => setEditData({...editData, expires_at: e.target.value ? new Date(e.target.value).toISOString() : null})}
                            className="text-xs h-8"
                            data-testid="edit-user-expires"
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button onClick={handleUpdateUser} className="text-xs h-8" data-testid="edit-user-save">حفظ</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                  <Dialog open={passwordDialog} onOpenChange={setPasswordDialog}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" className="text-xs h-7" data-testid="change-password-btn">
                        <Key className="h-3 w-3 ml-1" />
                        كلمة المرور
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-sm">
                      <DialogHeader>
                        <DialogTitle className="text-sm">تغيير كلمة المرور</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <Label className="text-xs">كلمة مرور جديدة</Label>
                          <Input
                            type="text"
                            placeholder="أدخل كلمة المرور"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="text-xs h-8"
                            data-testid="new-password-input"
                          />
                        </div>
                        <Button 
                          className="w-full text-xs h-8" 
                          onClick={() => handleChangePassword(false)}
                          disabled={!newPassword}
                          data-testid="set-password-btn"
                        >
                          تعيين
                        </Button>
                        <Separator />
                        <Button 
                          variant="outline" 
                          className="w-full text-xs h-8" 
                          onClick={() => handleChangePassword(true)}
                          data-testid="random-password-btn"
                        >
                          <RefreshCw className="h-3 w-3 ml-1" />
                          عشوائية
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {selectedUser ? (
                <div className="space-y-4">
                  {/* User Info */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">ID</Label>
                      <p className="font-mono text-xs">{selectedUser.id}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">البريد</Label>
                      <p className="text-xs">{selectedUser.email}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">الهاتف</Label>
                      <p className="text-xs" dir="ltr">{selectedUser.phone}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">التسجيل</Label>
                      <p className="text-xs">{new Date(selectedUser.created_at).toLocaleDateString('ar')}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">الحالة</Label>
                      <div className="flex items-center gap-2">
                        <Badge variant={selectedUser.is_trial ? "outline" : selectedUser.is_active ? "default" : "destructive"} className="text-xs">
                          {selectedUser.is_trial ? "تجريبي" : selectedUser.is_active ? "مفعل" : "معطل"}
                        </Badge>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        {selectedUser.is_trial ? "الطلبات التجريبية" : "الطلبات المتبقية"}
                      </Label>
                      <p className="text-xs font-bold">
                        {selectedUser.is_trial 
                          ? (selectedUser.trial_count || 0) 
                          : selectedUser.subscription_type === "daily"
                            ? `${Math.max(0, (selectedUser.daily_requests_limit || 0) - (selectedUser.daily_requests_used || 0))} / ${selectedUser.daily_requests_limit || 0} يومي`
                            : (selectedUser.max_requests ?? "∞")
                        }
                      </p>
                    </div>
                    {selectedUser.subscription_type === "daily" && !selectedUser.is_trial && (
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">نوع الاشتراك</Label>
                        <Badge variant="outline" className="text-xs">اشتراك يومي</Badge>
                      </div>
                    )}
                    {selectedUser.expires_at && (
                      <div className="space-y-1 col-span-2">
                        <Label className="text-xs text-muted-foreground">تاريخ الانتهاء</Label>
                        <p className="text-xs">{new Date(selectedUser.expires_at).toLocaleString('ar')}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">API Token</Label>
                    <div className={`p-2 rounded ${codeBoxClass}`}>
                      <p className="font-mono text-xs break-all">{selectedUser.api_token}</p>
                    </div>
                  </div>

                  <Separator />

                  {/* User Reports */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium text-xs">سجل العمليات</h3>
                      <Badge variant="outline" className="text-xs">{userReports.length} سجل</Badge>
                    </div>
                    
                    {/* Search in logs */}
                    <div className="relative mb-3">
                      <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                      <Input
                        placeholder="بحث في السجلات (آيدي اللاعب، النوع، الخطأ)..."
                        value={logSearchQuery}
                        onChange={(e) => handleLogSearch(e.target.value)}
                        className="text-xs h-8 pr-9"
                        data-testid="log-search-input"
                      />
                    </div>
                    
                    <ScrollArea className="h-64 md:h-48">
                      <div className="space-y-2">
                        {userReports.map((log, i) => (
                          <div key={i} className="p-2 md:p-3 bg-muted/50 rounded-lg">
                            {/* Header Row */}
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1">
                              <div className="flex items-center gap-2">
                                {log.success !== false ? (
                                  <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                                ) : (
                                  <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                                )}
                                <div className="min-w-0">
                                  <p className="font-medium text-xs">{log.type}</p>
                                  <p className="text-xs text-muted-foreground truncate">{log.player_id}</p>
                                </div>
                              </div>
                              <p className="text-xs text-muted-foreground mr-6 md:mr-0">
                                {new Date(log.created_at).toLocaleDateString('ar')}
                              </p>
                            </div>
                            
                            {/* Success Details */}
                            {log.success !== false && log.kokos_response && (
                              <div className="mt-2 p-2 bg-green-500/10 rounded text-xs border border-green-500/20">
                                <div className="flex flex-col gap-1">
                                  {log.kokos_response.name && (
                                    <div className="flex gap-2">
                                      <span className="text-muted-foreground">اسم اللاعب:</span>
                                      <span className="text-green-400">{log.kokos_response.name}</span>
                                    </div>
                                  )}
                                  {log.kokos_response.productName && (
                                    <div className="flex gap-2">
                                      <span className="text-muted-foreground">الفئة:</span>
                                      <span className="text-green-400">{log.kokos_response.productName}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Error Details for Admin - Mobile Friendly */}
                            {log.success === false && (log.error_code || log.error_message || log.error) && (
                              <div className="mt-2 p-2 bg-red-500/10 rounded text-xs border border-red-500/20">
                                <div className="flex flex-col gap-1">
                                  <div className="flex flex-wrap items-center gap-1">
                                    <span className="font-mono text-red-400 bg-red-500/20 px-1 rounded">{log.error_code || 'ERROR'}</span>
                                  </div>
                                  <p className="text-red-300 break-words">{log.error_message || log.error || 'خطأ غير معروف'}</p>
                                </div>
                                {log.raw_error && (
                                  <details className="mt-2">
                                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground text-xs py-1">
                                      ▶ عرض التفاصيل الكاملة
                                    </summary>
                                    <div className="mt-2 p-2 bg-black/30 rounded overflow-hidden">
                                      <pre className="text-xs whitespace-pre-wrap break-all text-muted-foreground max-h-32 overflow-y-auto" dir="ltr">
                                        {log.raw_error}
                                      </pre>
                                    </div>
                                  </details>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                        {userReports.length === 0 && (
                          <p className="text-center text-muted-foreground py-6 text-xs">لا توجد سجلات</p>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p className="text-xs">اختر مستخدم</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  
  if (adminOnly) {
    if (!isAdmin) return <Navigate to="/admin/login" />;
  } else {
    if (!user && !isAdmin) return <Navigate to="/login" />;
  }
  
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <div className="App min-h-screen bg-background text-foreground">
          <BrowserRouter>
            <Navbar />
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/docs" element={<DocsPage />} />
              <Route path="/playground" element={
                <ProtectedRoute>
                  <PlaygroundPage />
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute adminOnly>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
            </Routes>
            <FloatingContact />
          </BrowserRouter>
          <Toaster 
            position="top-center" 
            richColors
            expand={false}
            toastOptions={{
              className: 'text-xs',
              style: {
                maxWidth: '90vw',
                margin: '0 auto',
              },
            }}
          />
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
